import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { createPartner, updatePartner } from '../../redux/features/partnerSlice';
import { toast } from 'react-toastify';
import { X, Palette } from 'lucide-react';

const DEFAULT_PARTNER_COLOR = '#F97316';

const PartnerForm = ({ isEdit, partner, onClose }) => {
  const [formData, setFormData] = useState({
    companyName: '',
    contactPerson: '',
    email: '',
    phone: '',
    color: DEFAULT_PARTNER_COLOR,
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: ''
    },
    commissionRate: 0,
    taxId: '',
    registrationNumber: '',
    bankAccount: '',
    notes: '',
    active: true,
    permissions: {
      canViewAllJobs: false,
      canEditJobs: false,
      canDeleteJobs: false,
      canViewClients: false,
      canEditClients: false,
      canViewWorkers: false,
      canViewStatus: true,
      canViewInventory: false
    }
  });

  const dispatch = useDispatch();

  useEffect(() => {
    if (isEdit && partner) {
      setFormData({
        companyName: partner.companyName || '',
        contactPerson: partner.contactPerson || '',
        email: partner.email || '',
        phone: partner.phone || '',
        address: partner.address || {
          street: '',
          city: '',
          postalCode: '',
          country: ''
        },
        commissionRate: partner.commissionRate !== undefined ? partner.commissionRate : 0,
        color: partner.color || DEFAULT_PARTNER_COLOR,
        taxId: partner.taxId || '',
        registrationNumber: partner.registrationNumber || '',
        bankAccount: partner.bankAccount || '',
        notes: partner.notes || '',
        active: partner.active !== undefined ? partner.active : true,
        permissions: {
          canViewAllJobs: partner.permissions?.canViewAllJobs === true,
          canEditJobs: partner.permissions?.canEditJobs === true,
          canDeleteJobs: partner.permissions?.canDeleteJobs === true,
          canViewClients: partner.permissions?.canViewClients === true,
          canEditClients: partner.permissions?.canEditClients === true,
          canViewWorkers: partner.permissions?.canViewWorkers === true,
          canViewStatus: partner.permissions?.canViewStatus !== false,
          canViewInventory: partner.permissions?.canViewInventory === true
        }
      });
    }
  }, [isEdit, partner]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('address.')) {
      const addressField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value
        }
      }));
    } else if (name.startsWith('permissions.')) {
      const permKey = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permKey]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.companyName.trim()) {
      toast.error('Naziv kompanije je obavezan');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Email je obavezan');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Telefon je obavezan');
      return;
    }
    if (formData.commissionRate < 0 || formData.commissionRate > 100) {
      toast.error('Procenat provizije mora biti između 0 i 100');
      return;
    }
    if (formData.color && !/^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(formData.color)) {
      toast.error('Boja mora biti u hex formatu (npr. #F97316)');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        color: formData.color || DEFAULT_PARTNER_COLOR,
        hasAccess: false
      };
      if (isEdit) {
        await dispatch(updatePartner({ id: partner._id, updatedPartnerData: payload }));
        toast.success('Partner je uspešno ažuriran');
      } else {
        await dispatch(createPartner({ partnerData: payload }));
        toast.success('Partner je uspešno dodat');
      }
      onClose();
    } catch (error) {
      toast.error(error.message || 'Greška pri čuvanju partnera');
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white">
          {isEdit ? 'Izmeni partnera' : 'Dodaj novog partnera'}
        </h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <X size={20} />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300">
              Naziv kompanije *
            </label>
            <input
              type="text"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">
              Kontakt osoba
            </label>
            <input
              type="text"
              name="contactPerson"
              value={formData.contactPerson}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">
              Telefon *
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">
              Provizija poslodavca (%) *
            </label>
            <input
              type="number"
              name="commissionRate"
              value={formData.commissionRate}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              min="0"
              max="100"
              step="0.1"
              required
            />
            <p className="mt-1 text-xs text-gray-500">
              Vaš udeo od profita — ne partnerov (npr. 20 = 20%)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">
              PIB
            </label>
            <input
              type="text"
              name="taxId"
              value={formData.taxId}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">
              Matični broj
            </label>
            <input
              type="text"
              name="registrationNumber"
              value={formData.registrationNumber}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300">
              Broj računa
            </label>
            <input
              type="text"
              name="bankAccount"
              value={formData.bankAccount}
              onChange={handleChange}
              className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="border-t border-gray-700 pt-4">
          <div className="rounded-md border border-gray-700 bg-gray-900/40 p-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="text-sm font-medium text-gray-300 flex items-center gap-1.5 shrink-0">
                <Palette className="h-4 w-4 text-gray-400" />
                Boja servisa
              </span>
              <input
                type="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="h-9 w-11 rounded-md border border-gray-600 bg-gray-700 cursor-pointer p-0.5 shrink-0"
                aria-label="Izaberi boju servisa"
              />
              <input
                type="text"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className="w-28 shrink-0 rounded-md border-gray-600 bg-gray-700 text-white text-sm px-2 py-1.5 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="#F97316"
                pattern="^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$"
              />
              <div
                className="h-9 w-9 rounded-full border border-gray-600 flex items-center justify-center shrink-0 text-white text-xs font-bold"
                style={{ backgroundColor: formData.color }}
                title="Pregled inicijala"
              >
                {(formData.companyName || 'SP').trim().substring(0, 2).toUpperCase()}
              </div>
              <p className="text-xs text-gray-500 mb-0 min-w-0">
                Boja za inicijale i prikaz partnera u listama i kalendaru
              </p>
            </div>
          </div>
        </div>

        {/* Address Fields */}
        <div className="border-t border-gray-700 pt-4">
          <h3 className="text-lg font-semibold text-white mb-3">Adresa</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-300">
                Ulica
              </label>
              <input
                type="text"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Grad
              </label>
              <input
                type="text"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Poštanski broj
              </label>
              <input
                type="text"
                name="address.postalCode"
                value={formData.address.postalCode}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300">
                Država
              </label>
              <input
                type="text"
                name="address.country"
                value={formData.address.country}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-300">
            Napomene
          </label>
          <textarea
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={3}
            className="mt-1 block w-full rounded-md border-gray-600 bg-gray-700 text-white shadow-sm focus:border-blue-500 focus:ring-blue-500"
          />
        </div>

        {/* Dozvole */}
        <div className="border-t border-gray-700 pt-4">
          <h3 className="text-lg font-semibold text-white mb-3">Dozvole</h3>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                name="permissions.canViewInventory"
                checked={formData.permissions?.canViewInventory === true}
                onChange={handleChange}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700 rounded"
              />
              <span className="text-sm text-gray-300">Može da vidi robu centralnog servisa (rezervni delovi)</span>
            </label>
          </div>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            name="active"
            checked={formData.active}
            onChange={handleChange}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700 rounded"
          />
          <label className="ml-2 block text-sm text-gray-300">
            Aktivan
          </label>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-300 bg-gray-700 hover:bg-gray-600 transition-colors"
          >
            Otkaži
          </button>
          <button
            type="submit"
            className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
          >
            {isEdit ? 'Sačuvaj izmene' : 'Dodaj partnera'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default PartnerForm;

